import type { AdminUser, AuthUser, BlockedIp, LoginAttempt, SiteContent } from "@/lib/types";
import { getToken } from "@/lib/auth";

export const API_BASE = (import.meta.env["VITE_API_BASE_URL"] as string | undefined) || "/api";

type Envelope<T> = { success: true; data: T } | { success: false; error: string };

export async function apiFetch<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const url = path.startsWith("http") ? path : `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const token = getToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
    // Fallback for hosts that strip the Authorization header before PHP.
    headers.set("X-Authorization", `Bearer ${token}`);
  }


  const res = await fetch(url, { ...options, headers });
  let json: Envelope<T>;
  try {
    json = (await res.json()) as Envelope<T>;
  } catch {
    throw new Error(`Eroare de rețea (${res.status})`);
  }
  if (!json.success) throw new Error(json.error || "A apărut o eroare");
  return json.data;
}

export async function apiUpload(file: Blob, name: string): Promise<{ url: string }> {
  const fd = new FormData();
  fd.append("file", file, name);
  return apiFetch<{ url: string }>("/upload", { method: "POST", body: fd });
}

// ---------- Public ----------

export function getSiteContent(): Promise<SiteContent> {
  return apiFetch<SiteContent>("/content");
}

export function submitRegistration(data: {
  full_name: string;
  email: string;
  phone?: string;
  company?: string;
  job_title?: string;
  message?: string;
}): Promise<{ ok: true } | { ok: false; reason: "closed" }> {
  return apiFetch("/registrations", { method: "POST", body: JSON.stringify(data) });
}

// ---------- Auth ----------

export function login(email: string, password: string): Promise<{ token: string; user: AuthUser }> {
  return apiFetch("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
}

export function logout(): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>("/auth/logout", { method: "POST" }).catch(() => ({ ok: true as const }));
}

export async function getMe(): Promise<{ user: AuthUser }> {
  // Accept both { user: {...} } and a flat user payload for backwards compatibility.
  const data = await apiFetch<AuthUser | { user: AuthUser }>("/auth/me");
  const user = (data as { user?: AuthUser }).user ?? (data as AuthUser);
  return { user: { ...user, role: user.role || "admin" } };
}

// ---------- Users ----------

export function listAdminUsers(): Promise<AdminUser[]> {
  return apiFetch("/users");
}

export function createAdminUser(data: { email: string; password: string }): Promise<{ ok: true; id: string }> {
  return apiFetch("/users", { method: "POST", body: JSON.stringify(data) });
}

export function setUserPassword(data: { userId: string; password: string }): Promise<{ ok: true }> {
  return apiFetch(`/users/${data.userId}/password`, {
    method: "POST",
    body: JSON.stringify({ password: data.password }),
  });
}

export function deleteAdminUser(data: { userId: string }): Promise<{ ok: true }> {
  return apiFetch(`/users/${data.userId}`, { method: "DELETE" });
}

// ---------- Security ----------

export function checkAdminAccess(): Promise<{ blocked: boolean; ip: string | null }> {
  return apiFetch("/security/check-access");
}

export function recordLoginAttempt(data: {
  email?: string;
  success: boolean;
  reason?: string;
}): Promise<{ ok: true }> {
  return apiFetch("/security/login-attempts", { method: "POST", body: JSON.stringify(data) });
}

export function listLoginAttempts(): Promise<LoginAttempt[]> {
  return apiFetch("/security/login-attempts");
}

export function clearLoginAttempts(): Promise<{ ok: true }> {
  return apiFetch("/security/login-attempts/clear", { method: "POST" });
}

export function listBlockedIps(): Promise<BlockedIp[]> {
  return apiFetch("/security/blocked-ips");
}

export function blockIp(data: { ip: string; reason?: string }): Promise<{ ok: true }> {
  return apiFetch("/security/blocked-ips", { method: "POST", body: JSON.stringify(data) });
}

export function unblockIp(data: { id: string }): Promise<{ ok: true }> {
  return apiFetch(`/security/blocked-ips/${data.id}`, { method: "DELETE" });
}

// ---------- Settings ----------

export function listSettings(): Promise<{ key: string; value: string | null }[]> {
  return apiFetch("/settings");
}

export function saveSetting(key: string, value: string): Promise<{ ok: true }> {
  return apiFetch("/settings", { method: "POST", body: JSON.stringify({ [key]: value }) });
}

// ---------- Registrations ----------

export function listRegistrations(): Promise<Record<string, unknown>[]> {
  return apiFetch("/registrations");
}

export function deleteRegistration(id: string): Promise<{ ok: true }> {
  return apiFetch(`/registrations/${id}`, { method: "DELETE" });
}

export function updateRegistration(
  id: string,
  patch: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return apiFetch(`/registrations/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
}

export function resetRegistrations(): Promise<{ ok: true }> {
  return apiFetch("/registrations/reset", { method: "POST" });
}

export function exportRegistrationsUrl(): string {
  const token = getToken();
  const url = `${API_BASE}/registrations/export?format=csv`;
  return token ? `${url}&token=${encodeURIComponent(token)}` : url;
}

// ---------- Generic CRUD ----------

export function listRows(resource: string): Promise<Record<string, unknown>[]> {
  return apiFetch(`/${resource}`);
}

export function saveRow(
  resource: string,
  row: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  // content_blocks uses the unique `key` as the URL identifier.
  if (resource === "content_blocks" && row["id"] && row["key"]) {
    const { id: _id, key, ...body } = row;
    return apiFetch(`/content_blocks/${encodeURIComponent(String(key))}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  if (row["id"]) {
    const { id, ...body } = row;
    return apiFetch(`/${resource}/${id}`, { method: "PATCH", body: JSON.stringify(body) });
  }

  return apiFetch(`/${resource}`, { method: "POST", body: JSON.stringify(row) });
}

export function deleteRow(resource: string, id: string): Promise<{ ok: true }> {
  return apiFetch(`/${resource}/${id}`, { method: "DELETE" });
}

export function reorderRows(resource: string, ids: string[]): Promise<{ ok: true }> {
  return apiFetch(`/${resource}/reorder`, { method: "POST", body: JSON.stringify({ ids }) });
}
